import React from 'react';
import styled from 'styled-components';

interface ProjectIconProps {
  type: 'webtoon' | 'health' | 'tts' | 'voice' | 'legal' | 'vision' | 'research' | 'detection' | 'segmentation' | 'classification' | 'medical' | 'gan' | 'accessibility' | 'default';
  size?: number;
}

const IconContainer = styled.div<{ size: number }>`
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  perspective: 1000px;
  position: relative;
  cursor: pointer;
  
  /* Mobile optimization */
  @media (max-width: 768px) {
    width: ${props => props.size * 0.8}px;
    height: ${props => props.size * 0.8}px;
    perspective: 800px;
  }
  
  @media (max-width: 480px) {
    width: ${props => props.size * 0.7}px;
    height: ${props => props.size * 0.7}px;
    perspective: 600px;
  }
  
  /* Reduce motion for accessibility */
  @media (prefers-reduced-motion: reduce) {
    .icon-3d {
      transition: none !important;
      
      &:hover {
        transform: scale(1.05) !important;
      }
    }
  }
  
  .icon-3d {
    width: 100%;
    height: 100%;
    position: relative;
    transform-style: preserve-3d;
    transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    
    /* Enhanced hover with particle effect */
    &:hover {
      transform: rotateY(15deg) rotateX(-10deg) scale(1.1);
      
      .icon-layer {
        &:nth-child(1) {
          transform: translateZ(30px);
        }
        &:nth-child(2) {
          transform: translateZ(20px);
        }
        &:nth-child(3) {
          transform: translateZ(10px);
        }
      }
      
      .icon-glow {
        opacity: 1;
        transform: translateZ(-10px) scale(1.3);
      }
      
      .particles {
        opacity: 1;
      }
    }
    
    /* Mobile: Simpler hover */
    @media (max-width: 768px) {
      &:hover {
        transform: scale(1.08);
        
        .icon-layer {
          &:nth-child(1) {
            transform: translateZ(15px);
          }
          &:nth-child(2) {
            transform: translateZ(10px);
          }
          &:nth-child(3) {
            transform: translateZ(5px);
          }
        }
      }
    }
  }
  
  .icon-layer {
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .icon-glow {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    filter: blur(20px);
    opacity: 0.5;
    transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  /* Particle effect */
  .particles {
    position: absolute;
    width: 100%;
    height: 100%;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.6s ease;
    
    circle {
      animation: float 3s infinite ease-in-out;
      
      &:nth-child(2) {
        animation-delay: 0.5s;
      }
      &:nth-child(3) {
        animation-delay: 1s;
      }
    }
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }
`;

// Keep all existing icon components (Webtoon, Health, TTS, Legal, Vision, Research)
const WebtoonIcon = () => (
  <>
    <div className="icon-glow" style={{ 
      background: 'radial-gradient(circle, rgba(139, 92, 246, 0.6) 0%, transparent 70%)' 
    }} />
    <div className="particles">
      <svg width="100%" height="100%" viewBox="0 0 64 64">
        <circle cx="15" cy="10" r="2" fill="#8b5cf6" opacity="0.6" />
        <circle cx="50" cy="15" r="1.5" fill="#6366f1" opacity="0.5" />
        <circle cx="55" cy="50" r="2" fill="#a78bfa" opacity="0.4" />
      </svg>
    </div>
    <div className="icon-layer">
      <svg width="100%" height="100%" viewBox="0 0 64 64" fill="none">
        <defs>
          <linearGradient id="webtoonGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#8b5cf6', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#6366f1', stopOpacity: 1 }} />
          </linearGradient>
          <filter id="webtoonShadow">
            <feDropShadow dx="0" dy="4" stdDeviation="4" floodOpacity="0.5" floodColor="#8b5cf6"/>
          </filter>
        </defs>
        <rect x="8" y="12" width="48" height="40" rx="4" fill="url(#webtoonGrad1)" filter="url(#webtoonShadow)" />
        <rect x="12" y="16" width="18" height="14" rx="2" fill="white" fillOpacity="0.2" />
        <rect x="34" y="16" width="18" height="14" rx="2" fill="white" fillOpacity="0.2" />
        <rect x="12" y="34" width="40" height="14" rx="2" fill="white" fillOpacity="0.2" />
        <circle cx="52" cy="16" r="2" fill="#fff" opacity="0.8">
          <animate attributeName="opacity" values="0.8;0.3;0.8" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx="58" cy="22" r="1.5" fill="#fff" opacity="0.6">
          <animate attributeName="opacity" values="0.6;0.2;0.6" dur="1.5s" repeatCount="indefinite" />
        </circle>
      </svg>
    </div>
  </>
);

const HealthIcon = () => (
  <>
    <div className="icon-glow" style={{ 
      background: 'radial-gradient(circle, rgba(16, 185, 129, 0.6) 0%, transparent 70%)' 
    }} />
    <div className="particles">
      <svg width="100%" height="100%" viewBox="0 0 64 64">
        <circle cx="20" cy="20" r="1.5" fill="#10b981" opacity="0.5" />
        <circle cx="45" cy="25" r="1" fill="#06b6d4" opacity="0.4" />
      </svg>
    </div>
    <div className="icon-layer">
      <svg width="100%" height="100%" viewBox="0 0 64 64" fill="none">
        <defs>
          <linearGradient id="healthGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#10b981', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#06b6d4', stopOpacity: 1 }} />
          </linearGradient>
        </defs>
        <circle cx="32" cy="18" r="6" fill="url(#healthGrad)" />
        <ellipse cx="32" cy="38" rx="8" ry="16" fill="url(#healthGrad)" />
        <path d="M20 24 Q20 20, 24 20 Q28 20, 28 24 L32 32 L36 24 Q36 20, 40 20 Q44 20, 44 24" 
              stroke="#10b981" strokeWidth="2" fill="none">
          <animate attributeName="opacity" values="1;0.3;1" dur="1.5s" repeatCount="indefinite" />
        </path>
        <circle cx="18" cy="46" r="2" fill="#10b981">
          <animate attributeName="cy" values="46;42;46" dur="1s" repeatCount="indefinite" />
        </circle>
        <circle cx="46" cy="46" r="2" fill="#06b6d4">
          <animate attributeName="cy" values="46;42;46" dur="1s" begin="0.5s" repeatCount="indefinite" />
        </circle>
      </svg>
    </div>
  </>
);

const TTSIcon = () => (
  <>
    <div className="icon-glow" style={{ 
      background: 'radial-gradient(circle, rgba(6, 182, 212, 0.6) 0%, transparent 70%)' 
    }} />
    <div className="icon-layer">
      <svg width="100%" height="100%" viewBox="0 0 64 64" fill="none">
        <defs>
          <linearGradient id="ttsGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#06b6d4', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#3b82f6', stopOpacity: 1 }} />
          </linearGradient>
        </defs>
        <rect x="26" y="20" width="12" height="18" rx="6" fill="url(#ttsGrad)" />
        <path d="M20 34 Q20 44, 32 44 Q44 44, 44 34" stroke="#06b6d4" strokeWidth="3" fill="none" />
        <line x1="32" y1="44" x2="32" y2="52" stroke="#06b6d4" strokeWidth="3" />
        <line x1="24" y1="52" x2="40" y2="52" stroke="#06b6d4" strokeWidth="3" strokeLinecap="round" />
        <g opacity="0.7">
          <path d="M48 26 Q52 32, 48 38" stroke="#06b6d4" strokeWidth="2" fill="none">
            <animate attributeName="opacity" values="0.7;0.3;0.7" dur="1.5s" repeatCount="indefinite" />
          </path>
          <path d="M52 22 Q58 32, 52 42" stroke="#3b82f6" strokeWidth="2" fill="none">
            <animate attributeName="opacity" values="0.5;0.2;0.5" dur="1.5s" begin="0.3s" repeatCount="indefinite" />
          </path>
          <path d="M16 26 Q12 32, 16 38" stroke="#06b6d4" strokeWidth="2" fill="none">
            <animate attributeName="opacity" values="0.7;0.3;0.7" dur="1.5s" begin="0.6s" repeatCount="indefinite" />
          </path>
          <path d="M12 22 Q6 32, 12 42" stroke="#3b82f6" strokeWidth="2" fill="none">
            <animate attributeName="opacity" values="0.5;0.2;0.5" dur="1.5s" begin="0.9s" repeatCount="indefinite" />
          </path>
        </g>
      </svg>
    </div>
  </>
);

const LegalIcon = () => (
  <>
    <div className="icon-glow" style={{ 
      background: 'radial-gradient(circle, rgba(99, 102, 241, 0.6) 0%, transparent 70%)' 
    }} />
    <div className="icon-layer">
      <svg width="100%" height="100%" viewBox="0 0 64 64" fill="none">
        <defs>
          <linearGradient id="legalGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#6366f1', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#8b5cf6', stopOpacity: 1 }} />
          </linearGradient>
        </defs>
        <rect x="18" y="12" width="28" height="40" rx="3" fill="url(#legalGrad)" />
        <line x1="24" y1="22" x2="40" y2="22" stroke="white" strokeWidth="2" strokeOpacity="0.6" strokeLinecap="round" />
        <line x1="24" y1="28" x2="38" y2="28" stroke="white" strokeWidth="2" strokeOpacity="0.5" strokeLinecap="round" />
        <line x1="24" y1="34" x2="36" y2="34" stroke="white" strokeWidth="2" strokeOpacity="0.4" strokeLinecap="round" />
        <line x1="24" y1="40" x2="38" y2="40" stroke="white" strokeWidth="2" strokeOpacity="0.3" strokeLinecap="round" />
        <circle cx="48" cy="46" r="6" fill="#6366f1" opacity="0.8">
          <animate attributeName="cy" values="46;44;46" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx="16" cy="48" r="6" fill="#8b5cf6" opacity="0.8">
          <animate attributeName="cy" values="48;46;48" dur="2s" repeatCount="indefinite" />
        </circle>
        <line x1="16" y1="42" x2="48" y2="40" stroke="white" strokeWidth="2" strokeOpacity="0.7" />
      </svg>
    </div>
  </>
);

const VisionIcon = () => (
  <>
    <div className="icon-glow" style={{ 
      background: 'radial-gradient(circle, rgba(234, 88, 12, 0.6) 0%, transparent 70%)' 
    }} />
    <div className="icon-layer">
      <svg width="100%" height="100%" viewBox="0 0 64 64" fill="none">
        <defs>
          <linearGradient id="visionGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#ea580c', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#f59e0b', stopOpacity: 1 }} />
          </linearGradient>
        </defs>
        <ellipse cx="32" cy="32" rx="20" ry="14" fill="url(#visionGrad)" />
        <circle cx="32" cy="32" r="8" fill="#1e293b" />
        <circle cx="32" cy="32" r="5" fill="#06b6d4">
          <animate attributeName="r" values="5;6;5" dur="3s" repeatCount="indefinite" />
        </circle>
        <circle cx="34" cy="30" r="2" fill="white" opacity="0.8" />
        <rect x="12" y="18" width="10" height="10" stroke="#ea580c" strokeWidth="2" fill="none" opacity="0.6">
          <animate attributeName="opacity" values="0.6;0.2;0.6" dur="2s" repeatCount="indefinite" />
        </rect>
        <rect x="42" y="36" width="8" height="8" stroke="#f59e0b" strokeWidth="2" fill="none" opacity="0.5">
          <animate attributeName="opacity" values="0.5;0.2;0.5" dur="2s" begin="0.5s" repeatCount="indefinite" />
        </rect>
      </svg>
    </div>
  </>
);

const ResearchIcon = () => (
  <>
    <div className="icon-glow" style={{ 
      background: 'radial-gradient(circle, rgba(99, 102, 241, 0.6) 0%, transparent 70%)' 
    }} />
    <div className="icon-layer">
      <svg width="100%" height="100%" viewBox="0 0 64 64" fill="none">
        <defs>
          <linearGradient id="researchGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#6366f1', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#3b82f6', stopOpacity: 1 }} />
          </linearGradient>
        </defs>
        <rect x="14" y="14" width="36" height="36" rx="4" fill="url(#researchGrad)" opacity="0.2" />
        <rect x="20" y="34" width="6" height="12" fill="#6366f1">
          <animate attributeName="height" values="12;16;12" dur="2s" repeatCount="indefinite" />
          <animate attributeName="y" values="34;30;34" dur="2s" repeatCount="indefinite" />
        </rect>
        <rect x="29" y="28" width="6" height="18" fill="#3b82f6">
          <animate attributeName="height" values="18;22;18" dur="2s" begin="0.3s" repeatCount="indefinite" />
          <animate attributeName="y" values="28;24;28" dur="2s" begin="0.3s" repeatCount="indefinite" />
        </rect>
        <rect x="38" y="22" width="6" height="24" fill="#8b5cf6">
          <animate attributeName="height" values="24;28;24" dur="2s" begin="0.6s" repeatCount="indefinite" />
          <animate attributeName="y" values="22;18;22" dur="2s" begin="0.6s" repeatCount="indefinite" />
        </rect>
        <path d="M 20 40 Q 30 30, 44 20" stroke="#10b981" strokeWidth="3" fill="none" strokeLinecap="round">
          <animate attributeName="stroke-dasharray" values="0 100;100 0" dur="3s" repeatCount="indefinite" />
        </path>
      </svg>
    </div>
  </>
);

// NEW ICON: Segmentation
const SegmentationIcon = () => (
  <>
    <div className="icon-glow" style={{ 
      background: 'radial-gradient(circle, rgba(236, 72, 153, 0.6) 0%, transparent 70%)' 
    }} />
    <div className="icon-layer">
      <svg width="100%" height="100%" viewBox="0 0 64 64" fill="none">
        <defs>
          <linearGradient id="segGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#ec4899', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#a855f7', stopOpacity: 1 }} />
          </linearGradient>
        </defs>
        {/* Grid background */}
        <rect x="12" y="12" width="40" height="40" rx="4" fill="url(#segGrad)" opacity="0.2" />
        {/* Segmented regions */}
        <path d="M 16 32 Q 24 28, 32 32 Q 40 36, 48 32" fill="#ec4899" opacity="0.6">
          <animate attributeName="d" values="M 16 32 Q 24 28, 32 32 Q 40 36, 48 32;M 16 32 Q 24 36, 32 32 Q 40 28, 48 32;M 16 32 Q 24 28, 32 32 Q 40 36, 48 32" dur="4s" repeatCount="indefinite" />
        </path>
        <ellipse cx="32" cy="22" rx="12" ry="8" fill="#a855f7" opacity="0.5" />
        <ellipse cx="32" cy="44" rx="10" ry="6" fill="#ec4899" opacity="0.7" />
        {/* Mask outlines */}
        <path d="M 18 20 L 28 20 L 28 28 L 18 28 Z" stroke="white" strokeWidth="2" fill="none" opacity="0.6">
          <animate attributeName="opacity" values="0.6;0.3;0.6" dur="2s" repeatCount="indefinite" />
        </path>
        <path d="M 36 24 L 46 24 L 46 32 L 36 32 Z" stroke="white" strokeWidth="2" fill="none" opacity="0.5">
          <animate attributeName="opacity" values="0.5;0.2;0.5" dur="2s" begin="0.5s" repeatCount="indefinite" />
        </path>
      </svg>
    </div>
  </>
);

// NEW ICON: Classification
const ClassificationIcon = () => (
  <>
    <div className="icon-glow" style={{ 
      background: 'radial-gradient(circle, rgba(245, 158, 11, 0.6) 0%, transparent 70%)' 
    }} />
    <div className="icon-layer">
      <svg width="100%" height="100%" viewBox="0 0 64 64" fill="none">
        <defs>
          <linearGradient id="classGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#f59e0b', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#ef4444', stopOpacity: 1 }} />
          </linearGradient>
        </defs>
        {/* Neural network nodes */}
        <circle cx="16" cy="20" r="4" fill="#f59e0b" />
        <circle cx="16" cy="32" r="4" fill="#f59e0b" />
        <circle cx="16" cy="44" r="4" fill="#f59e0b" />
        
        <circle cx="32" cy="24" r="5" fill="#fb923c" />
        <circle cx="32" cy="40" r="5" fill="#fb923c" />
        
        <circle cx="48" cy="32" r="6" fill="#ef4444">
          <animate attributeName="r" values="6;7;6" dur="2s" repeatCount="indefinite" />
        </circle>
        
        {/* Connections */}
        <line x1="20" y1="20" x2="27" y2="24" stroke="#f59e0b" strokeWidth="2" opacity="0.4">
          <animate attributeName="opacity" values="0.4;0.7;0.4" dur="2s" repeatCount="indefinite" />
        </line>
        <line x1="20" y1="32" x2="27" y2="26" stroke="#f59e0b" strokeWidth="2" opacity="0.3" />
        <line x1="20" y1="44" x2="27" y2="38" stroke="#f59e0b" strokeWidth="2" opacity="0.3" />
        
        <line x1="37" y1="24" x2="42" y2="30" stroke="#fb923c" strokeWidth="2" opacity="0.5">
          <animate attributeName="opacity" values="0.5;0.8;0.5" dur="2s" begin="0.3s" repeatCount="indefinite" />
        </line>
        <line x1="37" y1="40" x2="42" y2="34" stroke="#fb923c" strokeWidth="2" opacity="0.5" />
        
        {/* Labels */}
        <text x="52" y="28" fill="white" fontSize="8" opacity="0.6">A</text>
        <text x="52" y="36" fill="white" fontSize="8" opacity="0.6">I</text>
      </svg>
    </div>
  </>
);

// NEW ICON: Medical
const MedicalIcon = () => (
  <>
    <div className="icon-glow" style={{ 
      background: 'radial-gradient(circle, rgba(239, 68, 68, 0.6) 0%, transparent 70%)' 
    }} />
    <div className="icon-layer">
      <svg width="100%" height="100%" viewBox="0 0 64 64" fill="none">
        <defs>
          <linearGradient id="medicalGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#ef4444', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#dc2626', stopOpacity: 1 }} />
          </linearGradient>
        </defs>
        {/* Medical cross */}
        <rect x="28" y="16" width="8" height="32" rx="2" fill="url(#medicalGrad)" />
        <rect x="16" y="28" width="32" height="8" rx="2" fill="url(#medicalGrad)" />
        
        {/* Pulse line */}
        <path d="M 10 50 L 18 50 L 22 40 L 26 55 L 30 45 L 34 50 L 54 50" 
              stroke="#ef4444" strokeWidth="2.5" fill="none" strokeLinecap="round">
          <animate attributeName="stroke-dasharray" values="0 100;100 0" dur="2s" repeatCount="indefinite" />
        </path>
        
        {/* Heartbeat dots */}
        <circle cx="12" cy="16" r="2" fill="#ef4444" opacity="0.8">
          <animate attributeName="opacity" values="0.8;0.2;0.8" dur="1.5s" repeatCount="indefinite" />
          <animate attributeName="r" values="2;3;2" dur="1.5s" repeatCount="indefinite" />
        </circle>
        <circle cx="52" cy="18" r="2" fill="#dc2626" opacity="0.6">
          <animate attributeName="opacity" values="0.6;0.2;0.6" dur="1.5s" begin="0.5s" repeatCount="indefinite" />
          <animate attributeName="r" values="2;3;2" dur="1.5s" begin="0.5s" repeatCount="indefinite" />
        </circle>
      </svg>
    </div>
  </>
);

// NEW ICON: GAN/Generation
const GANIcon = () => (
  <>
    <div className="icon-glow" style={{ 
      background: 'radial-gradient(circle, rgba(168, 85, 247, 0.6) 0%, transparent 70%)' 
    }} />
    <div className="icon-layer">
      <svg width="100%" height="100%" viewBox="0 0 64 64" fill="none">
        <defs>
          <linearGradient id="ganGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#a855f7', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#6366f1', stopOpacity: 1 }} />
          </linearGradient>
        </defs>
        {/* Generator side */}
        <rect x="10" y="20" width="20" height="24" rx="3" fill="url(#ganGrad)" opacity="0.6" />
        <text x="15" y="34" fill="white" fontSize="10" fontWeight="bold">G</text>
        
        {/* Discriminator side */}
        <rect x="34" y="20" width="20" height="24" rx="3" fill="url(#ganGrad)" opacity="0.6" />
        <text x="39" y="34" fill="white" fontSize="10" fontWeight="bold">D</text>
        
        {/* Data flow arrows */}
        <path d="M 20 28 L 34 28" stroke="#a855f7" strokeWidth="2" markerEnd="url(#arrowhead)">
          <animate attributeName="stroke-dasharray" values="0 20;20 0" dur="1.5s" repeatCount="indefinite" />
        </path>
        <path d="M 44 36 L 30 36" stroke="#6366f1" strokeWidth="2" markerEnd="url(#arrowhead2)">
          <animate attributeName="stroke-dasharray" values="0 20;20 0" dur="1.5s" begin="0.5s" repeatCount="indefinite" />
        </path>
        
        {/* Arrow markers */}
        <defs>
          <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
            <polygon points="0 0, 10 3, 0 6" fill="#a855f7" />
          </marker>
          <marker id="arrowhead2" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
            <polygon points="0 0, 10 3, 0 6" fill="#6366f1" />
          </marker>
        </defs>
        
        {/* Generated output */}
        <rect x="22" y="48" width="20" height="8" rx="2" fill="#a855f7" opacity="0.4">
          <animate attributeName="opacity" values="0.4;0.7;0.4" dur="2s" repeatCount="indefinite" />
        </rect>
      </svg>
    </div>
  </>
);

// NEW ICON: Accessibility (for VISIONARY project)
const AccessibilityIcon = () => (
  <>
    <div className="icon-glow" style={{ 
      background: 'radial-gradient(circle, rgba(34, 197, 94, 0.6) 0%, transparent 70%)' 
    }} />
    <div className="icon-layer">
      <svg width="100%" height="100%" viewBox="0 0 64 64" fill="none">
        <defs>
          <linearGradient id="accessGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#22c55e', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#10b981', stopOpacity: 1 }} />
          </linearGradient>
        </defs>
        {/* Person head */}
        <circle cx="32" cy="18" r="6" fill="url(#accessGrad)" />
        
        {/* Person body (wheelchair user) */}
        <ellipse cx="32" cy="32" rx="8" ry="10" fill="url(#accessGrad)" opacity="0.7" />
        
        {/* Wheelchair wheel */}
        <circle cx="32" cy="44" r="10" stroke="#22c55e" strokeWidth="3" fill="none">
          <animate attributeName="stroke-dasharray" values="0 63;63 0" dur="3s" repeatCount="indefinite" />
        </circle>
        <circle cx="32" cy="44" r="6" fill="#22c55e" opacity="0.3" />
        
        {/* Spokes */}
        <line x1="32" y1="34" x2="32" y2="54" stroke="#10b981" strokeWidth="2" opacity="0.6">
          <animate attributeName="opacity" values="0.6;0.3;0.6" dur="2s" repeatCount="indefinite" />
        </line>
        <line x1="22" y1="44" x2="42" y2="44" stroke="#10b981" strokeWidth="2" opacity="0.5">
          <animate attributeName="opacity" values="0.5;0.3;0.5" dur="2s" begin="0.3s" repeatCount="indefinite" />
        </line>
        
        {/* Eye/Vision detection rays */}
        <path d="M 14 18 L 10 14" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" opacity="0.7">
          <animate attributeName="opacity" values="0.7;0.2;0.7" dur="1.5s" repeatCount="indefinite" />
        </path>
        <path d="M 50 18 L 54 14" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" opacity="0.6">
          <animate attributeName="opacity" values="0.6;0.2;0.6" dur="1.5s" begin="0.5s" repeatCount="indefinite" />
        </path>
        <path d="M 10 28 L 6 28" stroke="#10b981" strokeWidth="2" strokeLinecap="round" opacity="0.5">
          <animate attributeName="opacity" values="0.5;0.2;0.5" dur="1.5s" begin="0.25s" repeatCount="indefinite" />
        </path>
        <path d="M 54 28 L 58 28" stroke="#10b981" strokeWidth="2" strokeLinecap="round" opacity="0.5">
          <animate attributeName="opacity" values="0.5;0.2;0.5" dur="1.5s" begin="0.75s" repeatCount="indefinite" />
        </path>
      </svg>
    </div>
  </>
);

const DefaultIcon = () => (
  <>
    <div className="icon-glow" style={{ 
      background: 'radial-gradient(circle, rgba(99, 102, 241, 0.6) 0%, transparent 70%)' 
    }} />
    <div className="icon-layer">
      <svg width="100%" height="100%" viewBox="0 0 64 64" fill="none">
        <defs>
          <linearGradient id="defaultGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#6366f1', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#8b5cf6', stopOpacity: 1 }} />
          </linearGradient>
        </defs>
        <rect x="14" y="18" width="36" height="28" rx="4" fill="url(#defaultGrad)" />
        <circle cx="32" cy="32" r="8" fill="white" fillOpacity="0.3">
          <animate attributeName="r" values="8;10;8" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx="32" cy="32" r="4" fill="white" fillOpacity="0.6" />
      </svg>
    </div>
  </>
);

const iconComponents = {
  webtoon: WebtoonIcon,
  health: HealthIcon,
  tts: TTSIcon,
  voice: TTSIcon,
  legal: LegalIcon,
  vision: VisionIcon,
  research: ResearchIcon,
  detection: VisionIcon,
  segmentation: SegmentationIcon,
  classification: ClassificationIcon,
  medical: MedicalIcon,
  gan: GANIcon,
  accessibility: AccessibilityIcon,
  default: DefaultIcon
};

export const ProjectIcon: React.FC<ProjectIconProps> = ({ type, size = 50 }) => {
  const IconComponent = iconComponents[type] || iconComponents.default;
  
  return (
    <IconContainer size={size}>
      <div className="icon-3d">
        <IconComponent />
      </div>
    </IconContainer>
  );
};

